import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaExternalLinkAlt } from 'react-icons/fa';
import {
  EULA_PDF_PATH,
  EULA_STATEMENT,
  ensureAuth,
  getEulaStatus,
  markEulaAccepted,
  recordEulaAcceptance,
  updatePassword,
} from '../components/utils/eula';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [status, setStatus] = useState(null);
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const record = ensureAuth();
    if (!record?.id) {
      navigate('/login');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const nextStatus = await getEulaStatus(record.id);
        if (!cancelled) setStatus(nextStatus);
      } catch (err) {
        if (!cancelled) {
          setError('Could not check whether this account must accept the EULA.');
        }
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const eulaRequired = Boolean(status?.required);
  const mustAcceptNow = eulaRequired && !status?.accepted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const record = ensureAuth();
    if (!record?.id) {
      navigate('/login');
      return;
    }

    if (!passwordUpdated) {
      if (!oldPassword || !password || !passwordConfirm) {
        setError('Enter your current password and a new password.');
        return;
      }
      if (password.length < 8) {
        setError('The new password must be at least 8 characters.');
        return;
      }
      if (password !== passwordConfirm) {
        setError('The new password and confirmation do not match.');
        return;
      }
    }

    if (eulaRequired && !accepted) {
      setError(`Tick "${EULA_STATEMENT}" before continuing.`);
      return;
    }

    setSubmitting(true);
    let didUpdatePassword = passwordUpdated;
    try {
      if (!didUpdatePassword) {
        await updatePassword(record.id, oldPassword, password, passwordConfirm);
        didUpdatePassword = true;
        setPasswordUpdated(true);
      }

      if (eulaRequired) {
        await recordEulaAcceptance({
          userId: record.id,
          roles: status?.roles || [],
          companyIds: status?.companyIds || [],
        });
        markEulaAccepted(record.id);
      }

      setSuccess(eulaRequired
        ? 'Password updated and your acceptance of the terms was recorded.'
        : 'Password updated.');
      setTimeout(() => navigate('/home'), 1200);
    } catch (err) {
      const data = err?.data || err?.response?.data;
      const fieldMessage = data && typeof data === 'object'
        ? Object.values(data).map((item) => (typeof item === 'string' ? item : item?.message)).filter(Boolean).join(' ')
        : '';
      const message = fieldMessage || err?.message || 'Could not update the password.';
      if (didUpdatePassword && eulaRequired) {
        setError(`Your password was changed, but the acceptance was not saved. ${message} Submit again to record it. Use the new password if you are asked to sign in.`);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-4xl font-bold text-[#0a1744] bg-gradient-to-r from-[#0a1744] to-[#A1E744] bg-clip-text text-transparent mb-3">
        Change password
      </h1>
      <p className="text-gray-600 mb-8">
        {eulaRequired
          ? 'This account is a Master User, Main Administrator, or ARC Chief. Changing the password records that you read and understood the terms and conditions, together with your IP address and the date and time.'
          : 'Choose a new password for this account.'}
      </p>

      {loadingStatus ? (
        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 animate-pulse h-40" />
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 space-y-5">
          {eulaRequired && status?.roles?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {status.roles.map((role) => (
                <span key={role} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#0a1744] text-white">
                  {role}
                </span>
              ))}
            </div>
          )}

          {status?.collectionMissing && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              The EulaAcceptances collection is not in PocketBase yet. Create it before this acceptance can be saved.
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          {success && (
            <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">{success}</div>
          )}

          {!passwordUpdated && (
            <>
              <PasswordField
                label="Current password"
                value={oldPassword}
                onChange={setOldPassword}
                visible={showOld}
                onToggle={() => setShowOld((value) => !value)}
              />
              <PasswordField
                label="New password"
                value={password}
                onChange={setPassword}
                visible={showPassword}
                onToggle={() => setShowPassword((value) => !value)}
              />
              <PasswordField
                label="Confirm new password"
                value={passwordConfirm}
                onChange={setPasswordConfirm}
                visible={showConfirm}
                onToggle={() => setShowConfirm((value) => !value)}
              />
            </>
          )}

          {eulaRequired && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-gray-700">End User Licence Agreement</label>
                <a
                  href={EULA_PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-[#0a1744] hover:underline"
                >
                  Open agreement
                  <FaExternalLinkAlt className="ml-1 text-xs" />
                </a>
              </div>
              <iframe
                title="End User Licence Agreement"
                src={`${EULA_PDF_PATH}#toolbar=1&navpanes=0`}
                className="w-full h-[70vh] min-h-[32rem] border border-gray-300 rounded-md bg-white"
              />
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(event) => setAccepted(event.target.checked)}
                  className="mt-1 h-4 w-4 text-[#A1E744] focus:ring-[#A1E744] border-gray-300 rounded"
                />
                <span className="text-sm text-gray-900">{EULA_STATEMENT}</span>
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            {!mustAcceptNow && (
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={submitting || (eulaRequired && !accepted)}
              className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : passwordUpdated ? 'Record acceptance' : eulaRequired ? 'Accept and change password' : 'Change password'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const PasswordField = ({ label, value, onChange, visible, onToggle }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="new-password"
        className="mt-1 block w-full px-3 py-2 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  </div>
);

export default ChangePassword;
